import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  SG_APV3_FETCH_CAMPAIGN,
  SG_APV3_GET_CAMPAIGN_SUMMARY,
  SG_APV3_ADD_MANAGERS,
  SG_APV3_ADD_SURVEY_MANAGERS,
  SG_APV3_REMOVE_SURVEY_MANAGERS,
  SG_GET_ORGANIZATION_GROUP,
  SG_APV3_GET_REPORTS,
  SG_APV3_FETCH_REPORT,
  SG_APV3_UPDATE_CAMPAIGN,
  SG_APV3_GET_EVENTS,
  SG_APV3_CREATE_EVENT,
  SG_APV3_LAUNCH_EVENT,
  SG_APV3_START_REPORT_GENERATION,
} from "constants/actions";
import EventsTable from "./EventsTable";
import ReportDeliveryViewModal from "./ReportDeliveryViewModal";
import ScheduleEventModal from "./ScheduleEventModal";
import AddManagersModal from "./AddManagersModal";
import CampaignStepper from "./CampaignStepper";
import ActionPlanBreadcrumb from "./ActionPlanBreadcrumb";

// Icon component for Action Planning
const ActionPlanList = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 12L9 12M21 6L9 6M21 18L9 18M5 12C5 12.5523 4.55228 13 4 13C3.44772 13 3 12.5523 3 12C3 11.4477 3.44772 11 4 11C4.55228 11 5 11.4477 5 12ZM5 6C5 6.55228 4.55228 7 4 7C3.44772 7 3 6.55228 3 6C3 5.44772 3.44772 5 4 5C4.55228 5 5 5.44772 5 6ZM5 18C5 18.5523 4.55228 19 4 19C3.44772 19 3 18.5523 3 18C3 17.4477 3.44772 17 4 17C4.55228 17 5 17.4477 5 18Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.03L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11.03C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const UserPlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M6,10V7H4V10H1V12H4V15H6V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z" />
  </svg>
);

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.main`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 24px 32px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  animation: ${fadeUp} 280ms ease-out both;
`;

const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0 0 0;
  color: #6b7280;
`;

const Crumb = styled.span`
  font-size: 14px;
  font-weight: ${p => (p.active ? 600 : 500)};
  color: ${p => (p.active ? "#111827" : "#6b7280")};
  cursor: ${p => (p.clickable ? "pointer" : "default")};
  transition: color 0.2s ease;
  &:hover { color: ${p => (p.clickable ? "#374151" : p.active ? "#111827" : "#6b7280")}; }
`;

const Separator = styled.span`
  color: #d1d5db;
`;

const Header = styled.header`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: start;
  padding: 8px 0 0 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 7px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
`;

// Removed StatusPill per design update (status indicator hidden in header)

const RightActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Panel = styled.section`
  border: none;
  border-radius: 0;
  background: #ffffff;
  padding: 16px 0 0 0;
`;

const PanelTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 18px;
`;

const ManagersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const TableWrapper = styled.div`
  position: relative;
  overflow: visible;
`;

const Table = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols};
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-top: 1px solid #f3f4f6;
  transition: all 0.2s;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  position: relative;
  z-index: ${p => p.$menuOpen ? 2999 : 'auto'};
  ${p => p.$menuOpen && `
    pointer-events: none;
    & > * {
      pointer-events: auto;
    }
  `}
  &:first-child { 
    border-top: none; 
    background: #fafafa; 
    font-weight: 600; 
    cursor: default;
  }
  ${p => p.$clickable && !p.$menuOpen && `
    &:hover {
      background: #f0f4ff;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(71, 109, 250, 0.08);
    }
  `}
`;

const HeaderClickable = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
`;

const Button = styled.button`
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff);
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
`;

const GhostButton = styled.button`
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
`;

const OptionsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NameText = styled.div`
  font-weight: 600;
  color: #111827;
`;

const EmailText = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const ProgressCell = styled.div`
  display: grid;
  gap: 6px;
`;

const ProgressTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PercentText = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #111827;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  position: relative;
  z-index: ${p => p.$menuOpen ? 5001 : 'auto'};
`;

const KebabButton = styled.button`
  height: 30px;
  width: 30px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  cursor: pointer;
  position: relative;
  z-index: ${p => p.$menuOpen ? 5002 : 'auto'};
`;

const Dropdown = styled.div`
  position: fixed;
  top: ${p => p.$top}px;
  right: ${p => p.$right}px;
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.08);
  overflow: hidden;
  z-index: 5000;
`;

const MenuItem = styled.button`
  width: 100%;
  text-align: left;
  background: #fff;
  border: none;
  padding: 10px 12px;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`;

const ButtonSmall = styled.button`
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff);
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  padding: 8px 12px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  width: 240px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const Select = styled.select`
  height: 30px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-weight: 600;
  font-size: 12px;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: ${(props) => (props.disabled ? "#f8f9fa" : "#476dfa")};
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.disabled ? "#aaa" : "white")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;

const PaginationInfo = styled.div`
  display: inline-flex;
  align-items: center;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  gap: 4px;
`;

const PageJumpInput = styled.input`
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
  display: inline-block;

  &:focus {
    border-color: #476dfa;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

function displayName(m) {
  if (!m) return "";
  const n = [m.first_name, m.last_name].filter(Boolean).join(" ");
  return m.full_name || n || m.email || `ID ${m.id}`;
}

function prettyProgressStatus(s) {
  if (!s) return 'Draft';
  const v = String(s).toLowerCase();
  if (v === 'completed') return 'Completed';
  if (v === 'at_risk' || v === 'at-risk') return 'At risk';
  if (v === 'on_track' || v === 'on-track') return 'On track';
  if (v === 'in_progress' || v === 'in-progress') return 'In progress';
  if (v === 'not_started' || v === 'not-started') return 'Not started';
  if (v === 'active') return 'Active';
  return 'Draft';
}

function hashString(str) {
  try {
    const s = String(str || '');
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (h << 5) - h + s.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  } catch (e) {
    return 0;
  }
}

function computeManagerMetricsEntry(managerMetrics) {
  // managerMetrics shape: {
  //   manager_id, events_completed, total_events, events_completion_pct,
  //   actions_count, latest_action_statuses[], latest_status_counts { name: count }
  // }
  const totalEvents = Number(managerMetrics?.total_events || 0);
  const completedEvents = Number(managerMetrics?.events_completed || 0);
  const eventsPct = totalEvents > 0 ? Math.max(0, Math.min(100, (completedEvents / totalEvents) * 100)) : 0;

  // Derive actions and status counts, with fallback to latest_action_statuses list
  let totalActions = Number(managerMetrics?.actions_count || 0);
  let statusCounts = managerMetrics?.latest_status_counts || {};
  if ((!totalActions || totalActions === 0) && Array.isArray(managerMetrics?.latest_action_statuses)) {
    totalActions = managerMetrics.latest_action_statuses.length;
    const counts = {};
    for (const name of managerMetrics.latest_action_statuses) {
      counts[name] = (counts[name] || 0) + 1;
    }
    statusCounts = counts;
  }
  const onTrackNames = ['Completed', 'On Track', 'In Progress'];
  const onTrackCount = onTrackNames.reduce((sum, name) => sum + Number(statusCounts?.[name] || 0), 0);
  const onTrackPct = totalActions > 0 ? Math.max(0, Math.min(100, (onTrackCount / totalActions) * 100)) : 0;

  // Per spec: action plan is considered Completed if action count > 0
  const hasCompletedPlan = totalActions > 0;

  return {
    events: { completed: completedEvents, total: totalEvents, percent: Number(eventsPct.toFixed(1)) },
    actions: { total: totalActions, onTrackPercent: Number(onTrackPct.toFixed(1)), completed: hasCompletedPlan },
  };
}

const DescriptionBox = styled.div`
  color: #4b5563;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  margin-top: -10px;
`;

const EditableWrap = styled.div`
  position: relative;
  &:hover {
    outline: 0;
  }
  &:hover .edit-hint {
    opacity: 1;
  }
`;

const EditHint = styled.span`
  position: absolute;
  right: 0;
  top: -18px;
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 2px 8px;
  opacity: 0;
  transition: opacity 120ms ease;
`;

const EditableInput = styled.input`
  font-size: 28px;
  font-weight: 700;
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 8px;
`;

const EditableTextarea = styled.textarea`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  color: #374151;
  resize: vertical;
`;


// Tabs
const TabsBar = styled.div`
  display: flex;
  border-bottom: 2px solid #F3F4F6;
  margin-top: 8px;
  margin-bottom: 24px;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.active ? '#3B82F6' : '#6B7280'};
  border-bottom: 2px solid ${props => props.active ? '#3B82F6' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    color: #3B82F6;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const panelEnter = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const TabPanelWrap = styled.div`
  animation: ${panelEnter} 220ms ease-out both;
`;


const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 16px;
  text-align: center;
`;

const StatLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
`;

const StatNumber = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 28px;
  color: #476dfa;
  margin-bottom: 4px;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #f3f4f6;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 6px;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${p => Math.min(Math.max(p.value || 0, 0), 100)}%;
  background: linear-gradient(90deg, #6b5cff, #2c6bed);
`;

const MiniProgressBar = styled(ProgressBar)`
  height: 6px;
  margin-top: 4px;
`;

const ProgressStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: ${p => (
    p.status === 'completed' ? '#065f46' :
    p.status === 'at_risk' ? '#92400e' :
    p.status === 'on_track' ? '#1e40af' :
    p.status === 'active' ? '#374151' : '#6b7280'
  )};
  background: ${p => (
    p.status === 'completed' ? '#ecfdf5' :
    p.status === 'at_risk' ? '#fffbeb' :
    p.status === 'on_track' ? '#eff6ff' :
    p.status === 'active' ? '#f3f4f6' : '#f9fafb'
  )};
  border: 1px solid ${p => (
    p.status === 'completed' ? '#a7f3d0' :
    p.status === 'at_risk' ? '#fde68a' :
    p.status === 'on_track' ? '#bfdbfe' : '#e5e7eb'
  )};
`;

const CompletionPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${p => (p.$completed ? '#059669' : '#6b7280')};
  &:before {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => (p.$completed ? '#10b981' : '#d1d5db')};
  }
`;

const ReportStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  color: ${p => {
    if (p.$status === 'sent' || p.$status === 'approved') return '#059669';
    if (p.$status === 'failed' || p.$status === 'rejected') return '#dc2626';
    if (p.$status === 'pending') return '#d97706';
    if (p.$status === 'draft') return '#2563eb';
    return '#6b7280';
  }};
  &:before {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => {
      if (p.$status === 'sent' || p.$status === 'approved') return '#10b981';
      if (p.$status === 'failed' || p.$status === 'rejected') return '#ef4444';
      if (p.$status === 'pending') return '#f59e0b';
      if (p.$status === 'draft') return '#3b82f6';
      return '#9ca3af';
    }};
  }
`;

// Lightweight modal components
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  display: grid;
  place-items: center;
  z-index: 1200;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.08);
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const ModalText = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: #4b5563;
`;

const ModalActions = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalGhostButton = styled(GhostButton)`
  height: 36px;
  padding: 0 12px;
  font-size: 13px;
`;

// Modal components matching ManageActiveSurvey style
const ConfirmationModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const ConfirmationModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ConfirmationModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const ConfirmationModalTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0;
`;

const ConfirmationModalContent = styled.div`
  padding: 20px 24px;
`;

const ConfirmationModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
`;

const ConfirmationCancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const ConfirmationDeleteButton = styled.button`
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 20px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const FormGroup = styled.div`
  margin-top: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const ToastWrap = styled.div`
  position: fixed;
  right: 24px;
  bottom: 20px;
  background: #111827;
  color: #ffffff;
  border-radius: 10px;
  border: 1px solid #1f2937;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 8px 18px rgba(0,0,0,0.15);
  z-index: 1400;
`;

function toIsoOrNull(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    return d.toISOString();
  } catch (e) { return null; }
}

function toDateInputValue(isoString) {
  if (!isoString) return "";
  try {
    if (typeof isoString === 'string') {
      const match = isoString.match(/^\d{4}-\d{2}-\d{2}/);
      if (match) return match[0];
    }
    const d = new Date(isoString);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch (e) { return ""; }
}

// Convert a server ISO timestamp (UTC) to a local time string suitable for <input type="datetime-local">
function toLocalDateTimeInput(isoString) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    // Adjust by timezone offset so toISOString() returns local wall time
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 16);
  } catch (e) { return ""; }
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const campaign = useSelector(s => s.action_planning_v3?.selectedCampaign);
  const loading = useSelector(s => s.action_planning_v3?.loading);
  const events = useSelector(s => s.action_planning_v3?.events);
  const lastCreatedEvent = useSelector(s => s.action_planning_v3?.selectedEvent);
  const orgGroups = useSelector((s) => s.organization_group?.organization_groups);
  const reports = useSelector(s => s.action_planning_v3?.reports);
  const location = useLocation();

  const managersCatalog = useMemo(() => {
    const list = Array.isArray(orgGroups) ? orgGroups : [];
    const arr = list.map(g => (typeof g.manager === 'object' ? g.manager : null)).filter(Boolean);
    const dedup = new Map();
    arr.forEach(m => { if (m && m.id && !dedup.has(m.id)) dedup.set(m.id, m); });
    return Array.from(dedup.values()).sort((a,b) => displayName(a).localeCompare(displayName(b)));
  }, [orgGroups]);

  // Map reports by manager ID
  const reportsByManagerId = useMemo(() => {
    const reportsList = reports?.results || [];
    const map = new Map();
    reportsList.forEach(report => {
      if (report?.manager?.id) {
        map.set(report.manager.id, report);
      }
    });
    return map;
  }, [reports]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pendingAddCount, setPendingAddCount] = useState(0);
  const [tempTitle, setTempTitle] = useState("");
  const [tempDesc, setTempDesc] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [openMenuFor, setOpenMenuFor] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRefs = useRef({});
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL or localStorage to avoid flicker on mount
    try {
      const allowed = new Set(['overview', 'managers', 'events']);
      const params = new URLSearchParams(location?.search || '');
      const fromUrl = params.get('tab');
      const key = `apv3.campaign.${id}.activeTab`;
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
      const next = allowed.has(fromUrl) ? fromUrl : (allowed.has(stored) ? stored : 'overview');
      return next;
    } catch {
      return 'overview';
    }
  }); // overview | managers | events
  const [showSettings, setShowSettings] = useState(false);
  const [showScheduleReport, setShowScheduleReport] = useState(false);
  const [savingReportSchedule, setSavingReportSchedule] = useState(false);
  const [showReportView, setShowReportView] = useState(false);

  // Keep URL/localStorage in sync when the active tab changes (no dependency on location.search to avoid loops)

  useEffect(() => {
    try {
      const key = `apv3.campaign.${id}.activeTab`;
      if (id) {
        window.localStorage.setItem(key, activeTab);
      }
      const currentParams = new URLSearchParams(location.search || '');
      const currentTab = currentParams.get('tab');
      if (currentTab !== activeTab) {
        navigate(`${location.pathname}?tab=${activeTab}`, { replace: true });
      }
      // Fire email logs fetch when switching to Events tab so it appears in Network panel
      if (activeTab === 'events' && id) {
        dispatch({ type: 'SG_APV3_GET_EMAIL_LOGS_BY_CAMPAIGN', payload: { campaign_id: id } });
      }
    } catch {}
  }, [activeTab, id, navigate, location.pathname]);

  // Simulated progress for demo (steps 1 & 2)
  const [simReportsGenerated, setSimReportsGenerated] = useState(null);
  const [simReportsApproved, setSimReportsApproved] = useState(null);

  // Managers table controls
  const [sortField, setSortField] = useState(null); // 'participation' | 'onTrack' | 'actionPlan' | null
  const [sortDir, setSortDir] = useState(null); // 'asc' | 'desc' | null
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [managerSearch, setManagerSearch] = useState("");
  const [pageInputValue, setPageInputValue] = useState("1");

  const managersWithMetrics = useMemo(() => {
    const managers = Array.isArray(campaign?.selected_managers) ? campaign.selected_managers : [];
    const metricsArray = Array.isArray(campaign?.metrics?.managers) ? campaign.metrics.managers : [];
    const metricsMap = new Map(metricsArray.map(mm => [mm.manager_id, mm]));
    return managers.map(m => {
      const mm = metricsMap.get(m.id) || {};
      const computed = computeManagerMetricsEntry(mm);
      return { m, metrics: computed };
    });
  }, [campaign]);

  const filteredManagers = useMemo(() => {
    const q = String(managerSearch || "").trim().toLowerCase();
    if (!q) return managersWithMetrics;
    return managersWithMetrics.filter(({ m }) => {
      const name = displayName(m).toLowerCase();
      const email = String(m?.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [managersWithMetrics, managerSearch]);

  const sortedManagers = useMemo(() => {
    if (!sortField || !sortDir) return filteredManagers;
    const copy = [...filteredManagers];
    copy.sort((a,b) => {
      const getVal = (x) => {
        if (sortField === 'participation') return x?.metrics?.events?.percent || 0;
        if (sortField === 'onTrack') return x?.metrics?.actions?.onTrackPercent || 0;
        if (sortField === 'actionPlan') return x?.metrics?.actions?.completed ? 1 : 0;
        return 0;
      };
      const av = getVal(a);
      const bv = getVal(b);
      if (sortDir === 'asc') return av - bv;
      if (sortDir === 'desc') return bv - av;
      return 0;
    });
    return copy;
  }, [filteredManagers, sortField, sortDir]);

  const totalItems = sortedManagers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pagedManagers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedManagers.slice(start, start + pageSize);
  }, [sortedManagers, currentPage, pageSize]);

  // Sync page input value with current page when it changes externally
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Handle page jump input
  const handlePageJump = (e) => {
    if (e.key === "Enter") {
      const pageNumber = parseInt(pageInputValue, 10);
      if (
        !isNaN(pageNumber) &&
        pageNumber >= 1 &&
        pageNumber <= totalPages
      ) {
        setPage(pageNumber);
      } else {
        // Reset to current page if invalid
        setPageInputValue(currentPage.toString());
      }
    }
  };

  // Handle blur - reset to current page if invalid
  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInputValue, 10);
    if (
      isNaN(pageNumber) ||
      pageNumber < 1 ||
      pageNumber > totalPages
    ) {
      setPageInputValue(currentPage.toString());
    }
  };

  const currentManagerIds = useMemo(() => (campaign?.selected_managers || []).map(m => m.id), [campaign]);
  const notYetManagers = useMemo(() => {
    const selectedSet = new Set(currentManagerIds);
    return managersCatalog.filter(m => !selectedSet.has(m.id));
  }, [managersCatalog, currentManagerIds]);

  useEffect(() => {
    if (id) {
      dispatch({ type: SG_APV3_FETCH_CAMPAIGN, payload: { id } });
      dispatch({ type: SG_GET_ORGANIZATION_GROUP, payload: { group_type: 'permission' } });
      dispatch({ type: SG_APV3_GET_EVENTS, payload: `campaign=${id}` });
      dispatch({ type: SG_APV3_GET_REPORTS, payload: `campaign=${id}` });
    }
  }, [id, dispatch]);

  useEffect(() => {
    setTempTitle(campaign?.name || "");
    setTempDesc(campaign?.description || "");
    setTempEndDate(toDateInputValue(campaign?.end_date));
  }, [campaign]);

  const submitAddManagers = (newIds) => {
    if (!newIds || newIds.length === 0) return;
    setAdding(true);
    // Use selected manager object ids directly
    const managerIds = Array.from(new Set(newIds));
    // Get the manager objects from the catalog to add to Redux immediately
    const managerObjects = managersCatalog.filter(m => managerIds.includes(m.id));
    setPendingAddCount(managerIds.length);
    dispatch({ type: SG_APV3_ADD_SURVEY_MANAGERS, payload: { id, manager_ids: managerIds, manager_objects: managerObjects } });
  };

  // After adding managers completes, close modal and toast (no refetch needed - Redux updated directly)
  useEffect(() => {
    if (adding && !loading) {
      setShowAddModal(false);
      const n = pendingAddCount || 0;
      if (n > 0) {
        setToastMessage(`Added ${n} manager${n === 1 ? '' : 's'}.`);
        window.setTimeout(() => setToastMessage(""), 2500);
      }
      setAdding(false);
      setPendingAddCount(0);
      // No need to refetch - managers are already in Redux
    }
  }, [adding, loading, pendingAddCount]);

  const removeManager = (managerId) => {
    const next = (currentManagerIds || []).filter(x => x !== managerId);
    dispatch({ type: SG_APV3_REMOVE_SURVEY_MANAGERS, payload: { id, manager_ids: [managerId] } });
    // No need to refetch - reducer updates Redux directly
  };

  const saveSettings = useCallback(() => {
    const body = { name: tempTitle || "", description: tempDesc || "", end_date: toIsoOrNull(tempEndDate) };
    dispatch({ type: SG_APV3_UPDATE_CAMPAIGN, payload: { id, body } });
    setShowSettings(false);
  }, [dispatch, id, tempTitle, tempDesc, tempEndDate]);

  // Archive action removed per design update

  // Determine current step based on campaign data (with simulated overrides for demo)
  const summary = campaign?.campaign_summary || campaign || {};
  const baseReportsGenerated = summary?.reports_generated ?? summary?.reports_count ?? 0;
  const baseReportsApproved = summary?.reports_approved ?? 0;
  const totalManagers = summary?.total_managers ?? (campaign?.selected_managers || []).length;
  const reportsGenerated = simReportsGenerated ?? baseReportsGenerated;
  const reportsApproved = simReportsApproved ?? baseReportsApproved;
  const reportScheduled = Boolean(campaign?.report_send_date);
  const actionPlanningScheduled = summary?.action_planning_scheduled ?? false;
  const checkinsScheduled = summary?.checkins_scheduled ?? false;
  const hasManagers = totalManagers > 0;
  const canShowEventsTab = hasManagers && (reportsGenerated > 0);

  // Derive existing report email template values if present on the campaign
  const { reportEmailSubject, reportEmailHeader } = useMemo(() => {
    try {
      const templates = campaign?.email_templates || {};
      return {
        reportEmailSubject: templates.email_subject || '',
        reportEmailHeader: templates.email_content || '',
      };
    } catch {
      return { reportEmailSubject: '', reportEmailHeader: '' };
    }
  }, [campaign]);

  let currentStepIndex = 0;
  if (totalManagers > 0) currentStepIndex = 1;
  if (reportsGenerated > 0) currentStepIndex = 2;
  if (reportScheduled) currentStepIndex = 3;
  if (actionPlanningScheduled) currentStepIndex = 4;
  if (checkinsScheduled) currentStepIndex = 5; // beyond last means done

  const steps = [
    {
      key: 'add_managers',
      title: 'Add managers',
      description: totalManagers > 0 ? `${totalManagers} manager${totalManagers === 1 ? '' : 's'} added` : 'No managers added',
      status: currentStepIndex > 0 ? 'complete' : currentStepIndex === 0 ? 'current' : 'upcoming',
    },
    {
      key: 'generate',
      title: 'Generate reports',
      description: totalManagers ? `${reportsGenerated}/${totalManagers} generated` : `${reportsGenerated} generated`,
      status: currentStepIndex > 1 ? 'complete' : currentStepIndex === 1 ? 'current' : 'upcoming',
    },
    {
      key: 'approve',
      title: 'Schedule reports',
      description: reportScheduled ? 'Scheduled' : 'Not scheduled',
      status: currentStepIndex > 2 ? 'complete' : currentStepIndex === 2 ? 'current' : 'upcoming',
    },
    {
      key: 'schedule_ap',
      title: 'Schedule action planning',
      description: actionPlanningScheduled ? 'Scheduled' : 'Not scheduled',
      status: currentStepIndex > 3 ? 'complete' : currentStepIndex === 3 ? 'current' : 'upcoming',
    },
    {
      key: 'schedule_checkins',
      title: 'Schedule check-ins',
      description: checkinsScheduled ? 'Scheduled' : 'Not scheduled',
      status: currentStepIndex > 4 ? 'complete' : currentStepIndex === 4 ? 'current' : 'upcoming',
    },
  ];

  // Manager table column visibility based on step
  const showReportStatusCol = currentStepIndex <= 3; // Steps 0-3 show Report Status
  const showMetricsCols = !(currentStepIndex >= 1 && currentStepIndex <= 3); // Hide metrics at steps 1-3
  const gridCols = useMemo(() => {
    const nonActionCols = 1 + (showReportStatusCol ? 1 : 0) + (showMetricsCols ? 3 : 0); // Manager + optional columns
    if (nonActionCols <= 1) return '1fr 80px';
    return `repeat(${nonActionCols}, 1fr) 80px`;
  }, [showReportStatusCol, showMetricsCols]);

  // If prerequisites aren't met, prevent navigating to the Events tab
  useEffect(() => {
    if (!canShowEventsTab && activeTab === 'events') {
      setActiveTab('managers');
    }
  }, [canShowEventsTab, activeTab]);

  const primaryLabelByStep = ['Add Managers', 'Generate reports', 'Schedule reports', 'Schedule action planning', 'Schedule check-ins'];

  const handlePrimaryAction = () => {
    const step = steps[Math.min(currentStepIndex, steps.length - 1)]?.key;
    if (!step) return;
    // Navigate to existing routes for each step
    switch (step) {
      case 'add_managers':
        // Open the add managers modal
        setShowAddModal(true);
        break;
      case 'generate':
        // Mock confirmation flow instead of routing
        setShowGenerateConfirm(true);
        break;
      case 'approve':
        // Open schedule report modal instead of approve/send
        setShowScheduleReport(true);
        break;
      case 'schedule_ap':
        // For demo, open the schedule event modal instead of routing
        setShowScheduleModal(true);
        break;
      case 'schedule_checkins':
        // Manage active survey or scheduling page depending on backend flow
        navigate('/leader/audit/schedule-v2');
        break;
      default:
        break;
    }
  };

  const confirmGenerateReports = () => {
    setShowGenerateConfirm(false);
    if (!id) return;
    dispatch({ type: SG_APV3_START_REPORT_GENERATION, payload: { id } });
    // Optimistically advance stepper immediately
    setSimReportsGenerated(totalManagers || 0);
    // Refresh campaign summary to pick up server-side counts when available
    dispatch({ type: SG_APV3_GET_CAMPAIGN_SUMMARY, payload: { id } });
    setToastMessage(`Report generation started. We'll email you when it's done.`);
    window.setTimeout(() => setToastMessage("") , 2500);
  };

  const confirmApproveReports = () => {
    setShowApproveConfirm(false);
    const n = reportsGenerated || 0;
    setSimReportsApproved(n);
    setToastMessage(`Approved & sent ${n} report${n === 1 ? '' : 's'}.`);
    window.setTimeout(() => setToastMessage("") , 2500);
  };

  const handleViewReport = (manager) => {
    setOpenMenuFor(null);
    const report = reportsByManagerId.get(manager.id);
    if (report?.id) {
      // Fetch the report details via API
      dispatch({ type: SG_APV3_FETCH_REPORT, payload: { id: report.id } });
      console.log(`Fetching report with ID: ${report.id}`);
      // For now, just log the API call. You can add navigation or modal display here later.
    } else {
      console.warn('No report found for this manager');
    }
  };

  const handleSendReport = (manager) => {
    // No-op for now; just close the menu
    setOpenMenuFor(null);
  };

  const requestRemove = (manager) => {
    setRemoveTarget(manager);
    setOpenMenuFor(null);
  };

  // Update menu position when it opens
  useEffect(() => {
    if (!openMenuFor) {
      setMenuPosition(null);
      return;
    }

    const button = buttonRefs.current[openMenuFor];
    if (button) {
      const rect = button.getBoundingClientRect();
      // Always position dropdown below the button
      setMenuPosition({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    }
  }, [openMenuFor]);

  // Click-away to close actions menu
  useEffect(() => {
    if (!openMenuFor) return;
    function handleClick(e) {
      // Don't close if clicking on the dropdown itself
      const dropdown = document.querySelector('[data-dropdown-menu]');
      if (dropdown && dropdown.contains(e.target)) {
        return;
      }
      // Close on any document click if a menu is open
      setOpenMenuFor(null);
    }
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openMenuFor]);

  // After creating an event, close modal and refresh events
  useEffect(() => {
    if (creatingEvent && !loading) {
      setShowScheduleModal(false);
      setCreatingEvent(false);
      dispatch({ type: SG_APV3_GET_EVENTS, payload: `campaign=${id}` });
      setToastMessage('Event scheduled successfully.');
      window.setTimeout(() => setToastMessage(""), 2500);
    }
  }, [creatingEvent, loading, dispatch, id]);

  useEffect(() => {
    if (savingReportSchedule && !loading) {
      setShowScheduleReport(false);
      setSavingReportSchedule(false);
      dispatch({ type: SG_APV3_FETCH_CAMPAIGN, payload: { id } });
      setToastMessage('Report schedule saved.');
      window.setTimeout(() => setToastMessage(""), 2500);
    }
  }, [savingReportSchedule, loading, dispatch, id]);

  const submitScheduleEvent = (form) => {
    setCreatingEvent(true);
    dispatch({ type: SG_APV3_CREATE_EVENT, payload: form });
  };

  const submitScheduleReport = ({ report_send_date, custom_subject, header_text, require_admin_approval }) => {
    if (!id) return;
    setSavingReportSchedule(true);
    dispatch({
      type: SG_APV3_UPDATE_CAMPAIGN,
      payload: {
        id,
        body: { report_send_date, require_admin_approval, email_templates: { email_subject: custom_subject, email_content: header_text } },
      },
    });
  };

  const openScheduleReport = () => {
    setShowScheduleReport(true);
  };

  const viewReportSchedule = () => {
    setShowReportView(true);
  };

  const deleteReportSchedule = () => {
    if (!id) return;
    // Clear report_send_date and email_templates
    dispatch({
      type: SG_APV3_UPDATE_CAMPAIGN,
      payload: {
        id,
        body: { report_send_date: null, email_templates: { email_subject: '', email_content: '' } },
      },
    });
    // Refresh after update
    dispatch({ type: SG_APV3_FETCH_CAMPAIGN, payload: { id } });
    setToastMessage('Report schedule deleted.');
    window.setTimeout(() => setToastMessage("") , 2000);
  };

  const viewEvent = (ev) => {
    if (!ev?.id) return;
    navigate(`/leader/audit/action-plan-v3/campaign/${id}/event/${ev.id}`);
  };


  return (
    <Page>
      <ActionPlanBreadcrumb crumbs={[{ label: campaign?.name || 'Campaign' }]} />

      <Header>
        <HeaderContent>
          <IconWrapper>
            <ActionPlanList />
          </IconWrapper>
          <Title>{campaign?.name || 'Campaign'}</Title>
        </HeaderContent>
        <RightActions>
          <OptionsButton type="button" onClick={() => { setTempTitle(campaign?.name || ''); setTempDesc(campaign?.description || ''); setTempEndDate(toDateInputValue(campaign?.end_date)); setShowSettings(true); }}>
            <SettingsIcon />
            <span>Options</span>
          </OptionsButton>
        </RightActions>
      </Header>

      <div>
        <DescriptionBox>
          {campaign?.description || ''}
        </DescriptionBox>
      </div>

      <TabsBar role="tablist" aria-label="Campaign sections">
        <TabButton
          role="tab"
          aria-selected={activeTab === 'overview'}
          active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </TabButton>
        <TabButton
          role="tab"
          aria-selected={activeTab === 'managers'}
          active={activeTab === 'managers'}
          onClick={() => setActiveTab('managers')}
        >
          Managers
        </TabButton>
        {canShowEventsTab && (
          <TabButton
            role="tab"
            aria-selected={activeTab === 'events'}
            active={activeTab === 'events'}
            onClick={() => setActiveTab('events')}
          >
            Events
          </TabButton>
        )}
      </TabsBar>

      {activeTab === 'overview' && (
        <TabPanelWrap>
          <OverviewGrid>
            <CampaignStepper
              steps={steps}
              currentIndex={Math.min(currentStepIndex, steps.length - 1)}
              onPrimaryAction={handlePrimaryAction}
              primaryActionLabel={primaryLabelByStep[Math.min(currentStepIndex, steps.length - 1)]}
              caption={
                currentStepIndex >= steps.length
                  ? 'All steps completed! 🎉'
                  : 'Complete each step to finish your campaign.'
              }
            />

            <InfoGrid>
              <StatCard>
                <StatNumber>{campaign?.campaign_summary?.total_managers ?? (campaign?.selected_managers || []).length}</StatNumber>
                <StatLabel>Total Managers</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{campaign?.campaign_summary?.reports_generated ?? campaign?.reports_count ?? 0}</StatNumber>
                <StatLabel>Reports Generated</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{campaign?.campaign_summary?.actions_created ?? campaign?.actions_count ?? 0}</StatNumber>
                <StatLabel>Actions Created</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{campaign?.campaign_summary?.progress_percentage ?? campaign?.phase_progress ?? 0}%</StatNumber>
                <StatLabel>Phase Progress</StatLabel>
                <ProgressBar>
                  <ProgressFill value={campaign?.campaign_summary?.progress_percentage ?? campaign?.phase_progress ?? 0} />
                </ProgressBar>
              </StatCard>
            </InfoGrid>
          </OverviewGrid>

          {showGenerateConfirm && createPortal(
            (
              <ModalOverlay role="dialog" aria-modal="true" onClick={() => setShowGenerateConfirm(false)}>
                <ModalCard onClick={(e) => e.stopPropagation()}>
                  <ModalTitle>Generate reports</ModalTitle>
                  <ModalText>Generate reports for {totalManagers || 0} managers?</ModalText>
                  <ModalText>
                    Estimated time: ~{Math.max(1, totalManagers || 0) * 5} minutes (about 5 minutes per report).
                    We'll email you when the reports have all been generated.
                  </ModalText>
                  <ModalActions>
                    <ModalGhostButton type="button" onClick={() => setShowGenerateConfirm(false)}>Cancel</ModalGhostButton>
                    <Button type="button" onClick={confirmGenerateReports}>Generate</Button>
                  </ModalActions>
                </ModalCard>
              </ModalOverlay>
            ),
            document.body
          )}

          {showApproveConfirm && createPortal(
            (
              <ModalOverlay role="dialog" aria-modal="true" onClick={() => setShowApproveConfirm(false)}>
                <ModalCard onClick={(e) => e.stopPropagation()}>
                  <ModalTitle>Approve & send</ModalTitle>
                  <ModalText>Approve and send {reportsGenerated || 0} reports now?</ModalText>
                  <ModalActions>
                    <ModalGhostButton type="button" onClick={() => setShowApproveConfirm(false)}>Cancel</ModalGhostButton>
                    <Button type="button" onClick={confirmApproveReports}>Approve & send</Button>
                  </ModalActions>
                </ModalCard>
              </ModalOverlay>
            ),
            document.body
          )}
        </TabPanelWrap>
      )}

      {activeTab === 'managers' && (
        <TabPanelWrap>
          <Panel>
            <ManagersHeader>
              <PanelTitle>Managers {campaign?.campaign_summary?.total_managers ? `(${campaign.campaign_summary.total_managers})` : ''}</PanelTitle>
              <ControlsRow>
                <SearchContainer>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <SearchInput
                    type="text"
                    placeholder="Search managers..."
                    value={managerSearch}
                    onChange={(e) => { setPage(1); setManagerSearch(e.target.value); }}
                    aria-label="Search managers by name or email"
                  />
                </SearchContainer>
                <Select aria-label="Rows per page" value={String(pageSize)} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value) || 10); }}>
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                </Select>
                <ActionButton type="button" onClick={() => setShowAddModal(true)}>
                  <UserPlusIcon />
                  <span>Add Managers</span>
                </ActionButton>
              </ControlsRow>
            </ManagersHeader>
            <TableWrapper>
            <Table>
              <Row $cols={gridCols}>
                <div>Manager</div>
                {showReportStatusCol && <div>Report Status</div>}
                {showMetricsCols && (
                  <>
                    <HeaderClickable onClick={() => { setPage(1); setSortField('participation'); setSortDir(prev => (sortField !== 'participation' ? 'desc' : prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc')); }}>
                      <span>Event participation</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{sortField === 'participation' ? (sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '') : ''}</span>
                    </HeaderClickable>
                    <HeaderClickable onClick={() => { setPage(1); setSortField('actionPlan'); setSortDir(prev => (sortField !== 'actionPlan' ? 'desc' : prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc')); }}>
                      <span>Action plan</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{sortField === 'actionPlan' ? (sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '') : ''}</span>
                    </HeaderClickable>
                    <HeaderClickable onClick={() => { setPage(1); setSortField('onTrack'); setSortDir(prev => (sortField !== 'onTrack' ? 'desc' : prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc')); }}>
                      <span>On track</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{sortField === 'onTrack' ? (sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '') : ''}</span>
                    </HeaderClickable>
                  </>
                )}
                <div style={{ textAlign: 'right' }}>Actions</div>
              </Row>
                {pagedManagers.map(({ m, metrics }, index) => {
                const report = reportsByManagerId.get(m.id);
                  const isLastRow = index === pagedManagers.length - 1;
                  const isMenuOpen = openMenuFor === m.id;
                return (
                  <Row
                    key={m.id}
                    $cols={gridCols}
                    $clickable={true}
                    $menuOpen={isMenuOpen}
                    onClick={() => !isMenuOpen && navigate(`/leader/audit/action-plan-v3/campaign/${id}/manager/${m.id}`)}
                    title={isMenuOpen ? "" : "Click to view manager details"}
                  >
                    <NameBlock>
                      <NameText>{displayName(m)}</NameText>
                      <EmailText>{m.email}</EmailText>
                    </NameBlock>
                    {showReportStatusCol && (
                      <div>
                        {report ? (
                          <ReportStatusPill $status={report.status}>
                            {report.status}
                          </ReportStatusPill>
                        ) : (
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>No report</span>
                        )}
                      </div>
                    )}
                    {showMetricsCols && (
                      <>
                        <div>
                          <ProgressTopRow>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{metrics?.events?.completed || 0}/{metrics?.events?.total || 0}</div>
                            <PercentText>{metrics?.events?.percent || 0}%</PercentText>
                          </ProgressTopRow>
                          <MiniProgressBar>
                            <ProgressFill value={metrics?.events?.percent || 0} />
                          </MiniProgressBar>
                        </div>
                        <div>
                          <div>
                            <CompletionPill $completed={!!metrics?.actions?.completed}>
                              {metrics?.actions?.completed ? 'Completed' : 'Not completed'}
                            </CompletionPill>
                          </div>
                        </div>
                        <div>
                          <ProgressTopRow>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>On track</div>
                            <PercentText>{metrics?.actions?.onTrackPercent || 0}%</PercentText>
                          </ProgressTopRow>
                          <MiniProgressBar>
                            <ProgressFill value={metrics?.actions?.onTrackPercent || 0} />
                          </MiniProgressBar>
                        </div>
                      </>
                    )}
                    <ActionsCell $menuOpen={isMenuOpen}>
                      <KebabButton
                        ref={(el) => { buttonRefs.current[m.id] = el; }}
                        type="button"
                        aria-label="Actions"
                        $menuOpen={isMenuOpen}
                        onClick={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation && e.nativeEvent.stopImmediatePropagation(); setOpenMenuFor(openMenuFor === m.id ? null : m.id); }}
                      >
                        ···
                      </KebabButton>
                    </ActionsCell>
                  </Row>
                );
              })}
              {pagedManagers.length === 0 && (
                <Row>
                  <div style={{ gridColumn: '1 / -1', color: '#6b7280', fontSize: 13 }}>No managers match filters.</div>
                </Row>
              )}
            </Table>
            </TableWrapper>
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </PaginationButton>

                <PaginationInfo>
                  Page{" "}
                  <PageJumpInput
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInputValue}
                    onChange={(e) => setPageInputValue(e.target.value)}
                    onKeyDown={handlePageJump}
                    onBlur={handlePageInputBlur}
                  />{" "}
                  of {totalPages}
                </PaginationInfo>

                <PaginationButton
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationContainer>
            )}

          </Panel>

          {removeTarget && (
            <ModalOverlay role="dialog" aria-modal="true" onClick={() => setRemoveTarget(null)}>
              <ModalCard onClick={(e) => e.stopPropagation()}>
                <ModalTitle>Remove manager</ModalTitle>
                <ModalText>Are you sure you want to remove {displayName(removeTarget)} from this campaign?</ModalText>
                <ModalActions>
                  <GhostButton type="button" onClick={() => setRemoveTarget(null)}>Cancel</GhostButton>
                  <Button type="button" onClick={() => { const name = displayName(removeTarget); removeManager(removeTarget.id); setRemoveTarget(null); setToastMessage(`${name} removed`); window.setTimeout(() => setToastMessage("") , 2500); }}>Remove</Button>
                </ModalActions>
              </ModalCard>
            </ModalOverlay>
          )}
        </TabPanelWrap>
      )}

      {activeTab === 'events' && (
        <TabPanelWrap>
          <Panel>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <PanelTitle>Events {events?.count ? `(${events.count})` : ''}</PanelTitle>
              <div style={{ display: 'flex', gap: 8 }}>
                {!campaign?.report_send_date ? (
                  <ButtonSmall type="button" onClick={openScheduleReport}>Schedule Report</ButtonSmall>
                ) : (
                  <ButtonSmall type="button" onClick={() => setShowScheduleModal(true)}>Schedule Event</ButtonSmall>
                )}
              </div>
            </div>
            <EventsTable
              events={events?.results || []}
              onView={viewEvent}
              reportSendDate={campaign?.report_send_date}
              onEditReport={openScheduleReport}
              onViewReportSchedule={viewReportSchedule}
              onDeleteReportSchedule={deleteReportSchedule}
            />
          </Panel>
        </TabPanelWrap>
      )}

      {showSettings && (
        <ConfirmationModalOverlay onClick={() => setShowSettings(false)}>
          <ConfirmationModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>Campaign settings</ConfirmationModalTitle>
              <CloseButton onClick={() => setShowSettings(false)}>
                ×
              </CloseButton>
            </ConfirmationModalHeader>

            <ConfirmationModalContent>
              <FormGroup>
                <FormLabel>Title</FormLabel>
                <FormInput
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  placeholder="Enter campaign title"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  rows={3}
                  value={tempDesc}
                  onChange={(e) => setTempDesc(e.target.value)}
                  placeholder="Enter campaign description"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>End date</FormLabel>
                <FormInput
                  type="date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                />
              </FormGroup>
            </ConfirmationModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton onClick={() => setShowSettings(false)}>
                Cancel
              </ConfirmationCancelButton>
              <ConfirmationDeleteButton onClick={saveSettings}>
                Save
              </ConfirmationDeleteButton>
            </ConfirmationModalFooter>
          </ConfirmationModalContainer>
        </ConfirmationModalOverlay>
      )}

      {/* Global modals available from any tab */}
      <AddManagersModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={submitAddManagers}
        managers={notYetManagers}
        loading={adding}
      />

      <ScheduleEventModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSubmit={submitScheduleEvent}
        campaignId={id}
        existingEvents={events?.results || []}
        loading={creatingEvent && loading}
      />

      {showScheduleReport && (
        <ScheduleEventModal
          isOpen={true}
          onClose={() => setShowScheduleReport(false)}
          onSubmitReport={submitScheduleReport}
          campaignId={id}
          loading={savingReportSchedule}
          mode="report_delivery"
          initialReportDate={campaign?.report_send_date ? toLocalDateTimeInput(campaign.report_send_date) : ''}
          initialEmailSubject={reportEmailSubject}
          initialEmailHeader={reportEmailHeader}
        />
      )}

      <ReportDeliveryViewModal
        isOpen={!!showReportView && !!campaign?.report_send_date && new Date(campaign.report_send_date) <= new Date()}
        onClose={() => setShowReportView(false)}
        emailTemplates={campaign?.email_templates}
        reportSendDate={campaign?.report_send_date}
        reports={reports?.results || []}
      />

      {!!toastMessage && createPortal(
        (<ToastWrap role="status">{toastMessage}</ToastWrap>),
        document.body
      )}

      {/* Render dropdown menu via portal to escape table overflow */}
      {openMenuFor && menuPosition && createPortal(
        <Dropdown
          role="menu"
          data-dropdown-menu
          $top={menuPosition.top}
          $right={menuPosition.right}
          onClick={(e) => { e.stopPropagation(); }}
        >
          {(() => {
            const manager = pagedManagers.find(({ m }) => m.id === openMenuFor)?.m;
            if (!manager) return null;

            const report = reportsByManagerId.get(manager.id);
            const reportStatus = report?.status?.toLowerCase()?.trim();

            // Determine if report actions should be shown
            // Only show View/Send if report exists and status is valid (not failed/rejected/not generated/generating)
            const invalidStatuses = ['failed', 'rejected', 'not generated', 'not_generated', 'not-generated', 'generating'];
            const canViewOrSend = report && reportStatus && !invalidStatuses.includes(reportStatus);

            return (
              <>
                {canViewOrSend && (
                  <>
                    <MenuItem type="button" onClick={() => handleViewReport(manager)}>View Report</MenuItem>
                    <MenuItem type="button" onClick={() => handleSendReport(manager)}>Send Report</MenuItem>
                  </>
                )}
                <MenuItem type="button" onClick={() => requestRemove(manager)}>Remove</MenuItem>
              </>
            );
          })()}
        </Dropdown>,
        document.body
      )}
    </Page>
  );
}
